import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
} from "@mui/material";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";

const MeetingTimes = ({ user }) => {
  const [meetingTimes, setMeetingTimes] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedSlotRange, setSelectedSlotRange] = useState(null);

  const [newMeeting, setNewMeeting] = useState({
    meetingType: "",
    room: "Room 1",
    department: user?.department || "",
    username: user?.username || "",
    date: moment().format("YYYY-MM-DD"), // standardized
    start: moment().format("HH:mm"),
    end: moment().add(1, "hour").format("HH:mm"),
  });

  const fetchMeetingTimes = async () => {
    try {
      const response = await axios.post(
        `https://backenderpforseg.samielmetwali.com/fetch_meeting_times.php`
      );

      if (Array.isArray(response.data)) {
        setMeetingTimes(response.data);
      } else if (Array.isArray(response.data?.data)) {
        setMeetingTimes(response.data.data);
      } else {
        console.error("Unexpected data:", response.data);
        setMeetingTimes([]);
      }
    } catch (error) {
      console.error("Error fetching meeting times:", error);
      setMeetingTimes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetingTimes();
  }, []);

  useEffect(() => {
    // Fetch departments
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(
          "https://backenderpforseg.samielmetwali.com/getDepartments.php"
        );
        if (Array.isArray(response.data.departments)) {
          setDepartments(response.data.departments);
        }
      } catch (error) {
        console.error("خطأ أثناء تحميل الأقسام:", error);
      }
    };

    fetchDepartments();
    fetchMeetingTimes(); // Initial load
  }, []);
  const handleMeetingTypeChange = (value) => {
    let updatedRoom = newMeeting.room;

    if (MEETING_TYPES_ROOM1.includes(value)) {
      updatedRoom = "Room 1";
    } else if (MEETING_TYPES_ROOM2_3.includes(value)) {
      // لو المستخدم اختار Room 1 قبل كده، نغيرها تلقائيًا لـ Room 2
      updatedRoom = newMeeting.room === "Room 1" ? "Room 2" : newMeeting.room;
    }

    setNewMeeting({ ...newMeeting, meetingType: value, room: updatedRoom });
    fetchMeetingTimes(updatedRoom);
  };

  const handleRoomChange = (value) => {
    setNewMeeting({ ...newMeeting, room: value });
    fetchMeetingTimes(value);
  };

  const handleOpen = () => {
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const validateForm = () => {
    const tempErrors = {};
    if (!newMeeting.meetingType) tempErrors.meetingType = "نوع الاجتماع مطلوب";
    if (!newMeeting.room) tempErrors.room = "نوع الغرفة مطلوب";

    const start = moment(newMeeting.start, "HH:mm");
    const end = moment(newMeeting.end, "HH:mm");

    if (!start.isValid()) tempErrors.start = "وقت البدء غير صالح";
    if (!end.isValid()) tempErrors.end = "وقت الانتهاء غير صالح";
    if (end.isSameOrBefore(start))
      tempErrors.end = "وقت الانتهاء يجب أن يكون بعد وقت البدء";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleAddMeeting = async () => {
    if (!validateForm()) return;

    const dateForDB = newMeeting.date;
    const startDateTime = `${dateForDB} ${newMeeting.start}:00`;
    const endDateTime = `${dateForDB} ${newMeeting.end}:00`;

    try {
      const response = await axios.post(
        "https://backenderpforseg.samielmetwali.com/add_meeting_time.php",
        {
          meetingType: newMeeting.meetingType,
          room: newMeeting.room,
          date: dateForDB,
          start: startDateTime,
          end: endDateTime,
          department: newMeeting.department,
        }
      );

      if (response.data.success) {
        setMeetingTimes([
          ...meetingTimes,
          { ...newMeeting, id: response.data.id },
        ]);
        Swal.fire("نجاح", "تمت إضافة الاجتماع بنجاح", "success");
        handleClose();
        setNewMeeting((prev) => ({
          ...prev,
          meetingType: "",
          room: "",
          start: moment().format("HH:mm"),
          end: moment().add(1, "hour").format("HH:mm"),
        }));
      } else {
        handleClose();

        Swal.fire("خطأ", response.data.message || "فشل الإضافة", "error");
      }
    } catch (error) {
      Swal.fire("خطأ", "حدث خطأ أثناء إضافة الاجتماع", "error");
    }
  };

  const handleDeleteMeeting = async (id) => {
    const confirm = await Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لا يمكنك التراجع بعد الحذف!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذف",
      cancelButtonText: "إلغاء",
    });

    if (confirm.isConfirmed) {
      try {
        const response = await axios.post(
          "https://backenderpforseg.samielmetwali.com/delete_meeting_time.php",
          { id }
        );

        if (response.data.success) {
          setMeetingTimes((prev) =>
            prev.filter((meeting) => meeting.id !== id)
          );
          Swal.fire("تم الحذف!", "تم حذف الاجتماع بنجاح.", "success");
        } else {
          Swal.fire("خطأ", response.data.message || "فشل الحذف", "error");
        }
      } catch (error) {
        console.error("خطأ أثناء الحذف:", error);
        Swal.fire("خطأ", "حدث خطأ أثناء حذف الاجتماع", "error");
      }
    }
  };

  const START_HOUR = 8;
  const END_HOUR = 17;
  const SLOT_MINUTES = 15;

  const ROOMS = ["Room 1", "Room 2", "Room 3"];

  const getBusyRanges = (date, room) =>
    meetingTimes
      .filter((m) => m.date === date && m.room === room)
      .map((m) => ({
        start: moment(`${m.date} ${m.start}`, "YYYY-MM-DD HH:mm:ss"),
        end: moment(`${m.date} ${m.end}`, "YYYY-MM-DD HH:mm:ss"),
      }));

  const isSlotBusy = (slot, busyRanges) =>
    busyRanges.some(
      (range) => slot.isSameOrAfter(range.start) && slot.isBefore(range.end)
    );

  const generateTimeSlots = (date) => {
    const slots = [];
    let time = moment(`${date} ${START_HOUR}:00`, "YYYY-MM-DD HH:mm");
    const endTime = moment(`${date} ${END_HOUR}:00`, "YYYY-MM-DD HH:mm");

    while (time.isSameOrBefore(endTime)) {
      slots.push(moment(time)); // لاحظ: لحظة كاملة بتاريخ وساعة
      time.add(SLOT_MINUTES, "minutes");
    }

    return slots;
  };

  const MEETING_TYPES_ROOM1 = ["عام", "عملاء"];
  const MEETING_TYPES_ROOM2_3 = [
    "تنفيذي",
    "مشاريع",
    "تقييم",
    "عصف ذهني",
    "مخاطر",
    "أولويات",
  ];

  return (
    <Box>
      <Box>
        {ROOMS.map((room) => (
          <Box key={room} mb={5}>
            <Typography
              variant="h6"
              align="center"
              sx={{ mb: 2, color: "#6fd943" }}
            >
              الغرفة: {room}
            </Typography>

            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(85px, 1fr))"
              gap={1}
              justifyItems="center"
            >
              {generateTimeSlots(newMeeting.date).map((slot, idx) => {
                const busyMeeting = getBusyRanges(newMeeting.date, room).find(
                  (range) =>
                    slot.isSameOrAfter(range.start) && slot.isBefore(range.end)
                );

                const busy = !!busyMeeting;
                const isSelected =
                  selectedSlotRange &&
                  slot.isSameOrAfter(selectedSlotRange.start) &&
                  slot.isBefore(selectedSlotRange.end) &&
                  newMeeting.room === room;

                const isMine =
                  busy &&
                  meetingTimes.some(
                    (m) =>
                      m.date === newMeeting.date &&
                      m.room === room &&
                      slot.isSameOrAfter(
                        moment(`${m.date} ${m.start}`, "YYYY-MM-DD HH:mm:ss")
                      ) &&
                      slot.isBefore(
                        moment(`${m.date} ${m.end}`, "YYYY-MM-DD HH:mm:ss")
                      )
                  );

                const backgroundColor = busy
                  ? isMine
                    ? "#fdd835" // اللون الأصفر المحجوز من المستخدم نفسه
                    : "#ef5350" // الأحمر للحجوزات الأخرى
                  : isSelected
                  ? "#64b5f6"
                  : "#81c784"; // الأخضر للخانات الفارغة

                return (
                  <Box
                    key={idx}
                    onClick={() => {
                      if (!busy) {
                        const formattedStart = slot.format("HH:mm:ss");
                        const formattedEnd = slot
                          .clone()
                          .add(15, "minutes")
                          .format("HH:mm:ss");

                        setNewMeeting({
                          ...newMeeting,
                          start: formattedStart,
                          end: formattedEnd,
                          room,
                        });

                        setSelectedSlotRange({
                          start: slot.clone(),
                          end: slot.clone().add(15, "minutes"),
                        });

                        setOpen(true);
                      }
                    }}
                    sx={{
                      width: "100%",
                      backgroundColor,
                      cursor: busy ? "not-allowed" : "pointer",
                      color: "#000",
                      borderRadius: "10px",
                      padding: "10px 6px",
                      textAlign: "center",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      border: "1px solid #333",
                      boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
                      transform: "scale(1)",
                      transition: "all 0.3s ease",
                      animation: `fadeInZoom 0.4s ease ${idx * 40}ms both`,
                      "&:hover": {
                        backgroundColor:
                          !busy && !isSelected ? "#66bb6a" : undefined,
                        transform: !busy ? "scale(1.05)" : undefined,
                      },
                      "@keyframes fadeInZoom": {
                        from: { opacity: 0, transform: "scale(0.8)" },
                        to: { opacity: 1, transform: "scale(1)" },
                      },
                    }}
                  >
                    {slot.format("hh:mm A")}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        p={2}
        sx={{
          fontFamily: "Cairo, sans-serif",
          direction: "rtl",
          backgroundColor: "#1f2224",
          color: "white",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="50vh"
          >
            <CircularProgress sx={{ color: "#6fd943" }} />
          </Box>
        ) : (
          <Paper
            sx={{ mb: 2, p: 2, backgroundColor: "#141718", color: "white" }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "#",
                      "نوع الاجتماع",
                      "الغرفة",
                      "القسم",
                      "التاريخ",
                      "البدء",
                      "الانتهاء",
                      "العمليات",
                    ].map((col) => (
                      <TableCell
                        key={col}
                        align="center"
                        sx={{ color: "#6fd943" }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meetingTimes.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ color: "gray" }}
                      >
                        لا توجد بيانات متاحة
                      </TableCell>
                    </TableRow>
                  ) : (
                    meetingTimes.map((m, idx) => (
                      <TableRow key={idx}>
                        <TableCell align="center" sx={{ color: "#fff" }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff" }}>
                          {m.meetingType}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff" }}>
                          {m.room}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff" }}>
                          {departments.find(
                            (dept) => String(dept.id) === String(m.department)
                          )?.name ||
                            m.department ||
                            "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff" }}>
                          {moment(m.date, "YYYY-MM-DD").format("YYYY-MM-DD")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff" }}>
                          {moment(m.start, "HH:mm:ss").format("hh:mm A")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff" }}>
                          {moment(m.end, "HH:mm:ss").format("hh:mm A")}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            onClick={() => handleDeleteMeeting(m.id)}
                            color="error"
                            variant="contained"
                            size="small"
                          >
                            حذف
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              backgroundColor: "#1f2224",
              color: "white",
              borderRadius: 2,
              p: 2,
              width: { xs: "95%", sm: 500 },
            },
          }}
        >
          <DialogTitle sx={{ color: "#6fd943", fontWeight: "bold" }}>
            إضافة تذكرة اجتماع
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <FormControl fullWidth error={!!errors.meetingType}>
              <InputLabel sx={{ color: "white" }} id="meeting-type-label">
                نوع الاجتماع
              </InputLabel>
              <Select
                labelId="meeting-type-label"
                value={newMeeting.meetingType}
                onChange={(e) => handleMeetingTypeChange(e.target.value)}
                label="نوع الاجتماع"
                sx={{
                  color: "white",
                  backgroundColor: "#2a2d30",
                  borderRadius: 1,
                }}
              >
                <MenuItem value="" disabled>
                  اختر نوع الاجتماع
                </MenuItem>

                {newMeeting.room === "Room 1"
                  ? MEETING_TYPES_ROOM1.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type === "عام" ? "اجتماع عام" : "مع عملاء أو شركاء"}
                      </MenuItem>
                    ))
                  : MEETING_TYPES_ROOM2_3.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
              </Select>
              {errors.meetingType && (
                <Typography color="error" fontSize="0.8rem">
                  {errors.meetingType}
                </Typography>
              )}
            </FormControl>
            {/* 
            <FormControl fullWidth error={!!errors.room}>
              <InputLabel sx={{ color: "white", margin: "5px" }}>
                الغرفة
              </InputLabel>
              <Select
                value={newMeeting.room}
                onChange={(e) => handleRoomChange(e.target.value)}
                sx={{
                  color: "white",
                  backgroundColor: "#2a2d30",
                  borderRadius: 1,
                }}
                disabled={MEETING_TYPES_ROOM1.includes(newMeeting.meetingType)} // إخفاء التعديل لو الغرفة مفروضة
              >
                {MEETING_TYPES_ROOM1.includes(newMeeting.meetingType) && (
                  <MenuItem value="Room 1">Room 1</MenuItem>
                )}

                {MEETING_TYPES_ROOM2_3.includes(newMeeting.meetingType) && (
                  <>
                    <MenuItem value="Room 2">Room 2</MenuItem>
                    <MenuItem value="Room 3">Room 3</MenuItem>
                  </>
                )}
              </Select>
            </FormControl> */}

            {/* Department */}
            <FormControl fullWidth error={!!errors.department}>
              <InputLabel sx={{ color: "white" }}>القسم</InputLabel>
              <Select
                value={newMeeting.department}
                onChange={(e) =>
                  setNewMeeting({ ...newMeeting, department: e.target.value })
                }
                sx={{
                  color: "white",
                  backgroundColor: "#2a2d30",
                  borderRadius: 1,
                }}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.department && (
                <Typography color="error" fontSize="0.8rem">
                  {errors.department}
                </Typography>
              )}
            </FormControl>

            {/* <TextField
              label="التاريخ"
              type="date"
              fullWidth
              value={newMeeting.date}
              onChange={(e) =>
                setNewMeeting({
                  ...newMeeting,
                  date: e.target.value,
                })
              }
              InputLabelProps={{ shrink: true, style: { color: "white" } }}
              InputProps={{
                style: {
                  color: "white",
                  backgroundColor: "#2a2d30",
                  borderRadius: 4,
                },
              }}
            /> */}

            {/* <Box display="flex" gap={2}>
              <TextField
                label="وقت البدء"
                type="time"
                fullWidth
                value={newMeeting.start}
                onChange={(e) =>
                  setNewMeeting({ ...newMeeting, start: e.target.value })
                }
                error={!!errors.start}
                helperText={errors.start}
                InputLabelProps={{ shrink: true, style: { color: "white" } }}
                InputProps={{
                  style: {
                    color: "white",
                    backgroundColor: "#2a2d30",
                    borderRadius: 4,
                  },
                }}
              />
              <TextField
                label="وقت الانتهاء"
                type="time"
                fullWidth
                value={newMeeting.end}
                onChange={(e) =>
                  setNewMeeting({ ...newMeeting, end: e.target.value })
                }
                error={!!errors.end}
                helperText={errors.end}
                InputLabelProps={{ shrink: true, style: { color: "white" } }}
                InputProps={{
                  style: {
                    color: "white",
                    backgroundColor: "#2a2d30",
                    borderRadius: 4,
                  },
                }}
              />
            </Box> */}
          </DialogContent>
          <DialogActions sx={{ mt: 2 }}>
            <Button onClick={handleClose} sx={{ color: "white" }}>
              إلغاء
            </Button>
            <Button
              onClick={handleAddMeeting}
              sx={{
                backgroundColor: "#6fd943",
                color: "#fff",
                px: 3,
                fontWeight: "bold",
              }}
            >
              إضافة
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MeetingTimes;
